import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {
  PixelRatio,
  Pressable,
  type ScrollView,
  type StyleProp,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native'
import Animated, {
  interpolate,
  interpolateColor,
  type SharedValue,
  useAnimatedStyle,
  useDerivedValue,
} from 'react-native-reanimated'
import {useLingui} from '@lingui/react/macro'

import {DraggableScrollView} from '#/view/com/pager/DraggableScrollView'
import {BlockDrawerGesture} from '#/view/shell/BlockDrawerGesture'
import {atoms as a, tokens, useAlf, useTheme, utils, web} from '#/alf'
import {normalizeTextStyles} from '#/alf/typography'
import {Button, ButtonIcon} from '#/components/Button'
import {
  ArrowLeft_Stroke2_Corner0_Rounded as ArrowLeft,
  ArrowRight_Stroke2_Corner0_Rounded as ArrowRight,
} from '#/components/icons/Arrow'
import {Text} from '#/components/Typography'
import {IS_WEB} from '#/env'

export type TabPillItem = {
  key: string
  label: string
}

export function TabPills({
  tabs,
  selectedTab,
  dragProgress,
  onSelectTab,
  contentContainerStyle,
  gutterWidth = tokens.space.lg,
  getAccessibilityHint,
}: {
  tabs: TabPillItem[]
  selectedTab: string
  dragProgress: SharedValue<number>
  onSelectTab: (tab: string) => void
  contentContainerStyle?: StyleProp<ViewStyle>
  gutterWidth?: number
  /** Defaults to a hint that the tab shows notifications. */
  getAccessibilityHint?: (tab: TabPillItem) => string
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const {width: windowWidth} = useWindowDimensions()
  const listRef = useRef<React.ComponentRef<typeof ScrollView>>(null)
  const [totalWidth, setTotalWidth] = useState(0)
  const [scrollX, setScrollX] = useState(0)
  const [contentWidth, setContentWidth] = useState(0)
  const [tabOffsets, setTabOffsets] = useState<PillLayout[]>([])
  const contentRef = useRef<React.ComponentRef<typeof View>>(null)
  const tabRefs = useRef<Array<React.ComponentRef<typeof View> | null>>([])
  const didMeasure = useRef(false)
  const tabLayoutKey = tabs.map(tab => `${tab.key}:${tab.label}`).join('|')
  const tabCount = tabs.length

  /*
   * Also runs on layout events, since the whole bar can be mounted hidden
   * (e.g. `display: none` in an inactive web pager page) and only get real
   * dimensions when revealed, without re-rendering. Hidden measurements are
   * all zero, so they're skipped rather than overwriting the last good ones.
   */
  const measure = useCallback(() => {
    const viewportRect = listRef.current?.getBoundingClientRect()
    const contentRect = contentRef.current?.getBoundingClientRect()
    if (!viewportRect || !contentRect) return
    if (!hasSize(viewportRect) || !hasSize(contentRect)) return

    const layouts = Array.from({length: tabCount}, (_, index) => {
      const rect = tabRefs.current[index]?.getBoundingClientRect()
      if (!rect || !hasSize(rect)) return null
      return {
        x: rect.left - contentRect.left,
        y: rect.top - contentRect.top,
        width: rect.width,
        height: rect.height,
      }
    })
    if (layouts.some(layout => layout === null)) return

    const nextLayouts = layouts as PillLayout[]
    setTabOffsets(current =>
      areLayoutsEqual(current, nextLayouts) ? current : nextLayouts,
    )
    if (IS_WEB) {
      setTotalWidth(viewportRect.width)
      setContentWidth(contentRect.width)
    }

    const selectedIndex = tabs.findIndex(tab => tab.key === selectedTab)
    const selectedLayout = nextLayouts[selectedIndex]
    if (selectedLayout) {
      const centeredOffset =
        selectedLayout.x - (viewportRect.width / 2 - selectedLayout.width / 2)
      const maxOffset = Math.max(0, contentRect.width - viewportRect.width)
      listRef.current?.scrollTo({
        x: Math.min(maxOffset, Math.max(0, centeredOffset)),
        animated: didMeasure.current,
      })
    }
    didMeasure.current = true
  }, [selectedTab, tabCount, tabs])

  useLayoutEffect(() => {
    measure()
  }, [measure, tabLayoutKey, windowWidth])

  function handleSelectTab(index: number) {
    const tab = tabs[index]
    onSelectTab(tab.key)
  }

  const canScrollLeft = scrollX > 0
  const canScrollRight = Math.ceil(scrollX) < contentWidth - totalWidth
  // The scroll buttons line up with the pills, which are all the same height
  const pillTop = tabOffsets[0]?.y ?? 0
  const pillHeight = tabOffsets[0]?.height ?? 0
  const edgeMask = getEdgeMask({
    left: canScrollLeft,
    right: canScrollRight,
    width: gutterWidth + pillHeight + tokens.space.md,
  })
  /*
   * The active pill is a light grey under the usual text colour in the light
   * theme. In the dark themes (dim and dark) it's the highest contrast colour
   * instead, under inverted text.
   */
  const isDarkScheme = t.scheme === 'dark'
  const indicatorColor = isDarkScheme
    ? t.palette.contrast_1000
    : t.palette.contrast_50
  const activeLabelColor = isDarkScheme
    ? t.atoms.text_inverted.color
    : t.atoms.text.color
  const cleanupRef = useRef<(() => void) | null>(null)
  const isContinuouslyScrollingRef = useRef(false)

  function scrollLeft() {
    if (isContinuouslyScrollingRef.current) return
    if (listRef.current && canScrollLeft) {
      listRef.current.scrollTo({x: Math.max(0, scrollX - 200), animated: true})
    }
  }

  function scrollRight() {
    if (isContinuouslyScrollingRef.current) return
    if (listRef.current && canScrollRight) {
      const maxScroll = contentWidth - totalWidth
      listRef.current.scrollTo({
        x: Math.min(maxScroll, scrollX + 200),
        animated: true,
      })
    }
  }

  function startContinuousScroll(direction: 'left' | 'right') {
    cleanupRef.current?.()

    let holdTimeout: NodeJS.Timeout | null = null
    let animationFrame: number | null = null
    let isActive = true
    isContinuouslyScrollingRef.current = false

    const cleanup = () => {
      isActive = false
      if (holdTimeout) clearTimeout(holdTimeout)
      if (animationFrame) cancelAnimationFrame(animationFrame)
      cleanupRef.current = null
      setTimeout(() => {
        isContinuouslyScrollingRef.current = false
      }, 100)
    }

    cleanupRef.current = cleanup
    holdTimeout = setTimeout(() => {
      if (!isActive) return

      isContinuouslyScrollingRef.current = true
      let currentScrollPosition = scrollX

      const scroll = () => {
        if (!isActive || !listRef.current) return

        const scrollAmount = 3
        const maxScroll = contentWidth - totalWidth
        let newScrollX: number
        let canContinue = false

        if (direction === 'left' && currentScrollPosition > 0) {
          newScrollX = Math.max(0, currentScrollPosition - scrollAmount)
          canContinue = newScrollX > 0
        } else if (direction === 'right' && currentScrollPosition < maxScroll) {
          newScrollX = Math.min(maxScroll, currentScrollPosition + scrollAmount)
          canContinue = newScrollX < maxScroll
        } else {
          return
        }

        currentScrollPosition = newScrollX
        listRef.current.scrollTo({x: newScrollX, animated: false})

        if (canContinue && isActive) {
          animationFrame = requestAnimationFrame(scroll)
        }
      }

      scroll()
    }, 500)
  }

  function stopContinuousScroll() {
    cleanupRef.current?.()
  }

  useEffect(() => {
    return () => cleanupRef.current?.()
  }, [])

  return (
    <View style={[a.relative, a.flex_row]} accessibilityRole="tablist">
      <BlockDrawerGesture>
        <DraggableScrollView
          ref={listRef}
          onLayout={measure}
          style={web({maskImage: edgeMask})}
          showsHorizontalScrollIndicator={false}
          decelerationRate="fast"
          snapToOffsets={
            tabOffsets.length === tabs.length
              ? tabOffsets.map(layout => layout.x - gutterWidth)
              : undefined
          }
          onScroll={
            IS_WEB
              ? event => setScrollX(event.nativeEvent.contentOffset.x)
              : undefined
          }
          scrollEventThrottle={IS_WEB ? 16 : undefined}>
          <View
            ref={contentRef}
            onLayout={measure}
            style={[
              a.flex_row,
              a.gap_sm,
              {paddingHorizontal: gutterWidth},
              contentContainerStyle,
            ]}>
            {tabs.map((tab, index) => (
              <TabPill
                key={tab.key}
                elementRef={element => {
                  tabRefs.current[index] = element
                }}
                tab={tab}
                index={index}
                onLayout={measure}
                active={tab.key === selectedTab}
                onSelectTab={handleSelectTab}
                getAccessibilityHint={getAccessibilityHint}
              />
            ))}
            {tabOffsets.map((layout, index) => (
              <View
                key={`border-${tabs[index].key}`}
                accessible={false}
                pointerEvents="none"
                style={[
                  a.absolute,
                  a.rounded_full,
                  a.curve_continuous,
                  t.atoms.border_contrast_low,
                  {
                    // A touch see-through, so content scrolling under the header shows faintly
                    backgroundColor: utils.alpha(
                      t.atoms.bg.backgroundColor,
                      0.9,
                    ),
                    zIndex: 1,
                    borderWidth: 1,
                    left: layout.x,
                    top: layout.y,
                    width: layout.width,
                    height: layout.height,
                  },
                ]}></View>
            ))}
            {tabOffsets.length === tabs.length && (
              <PillIndicator
                layouts={tabOffsets}
                dragProgress={dragProgress}
                backgroundColor={indicatorColor}
                // Matches the fill, so no outline shows at the caps or middle
                borderColor={indicatorColor}
              />
            )}
            {tabOffsets.map((layout, index) => (
              <View
                key={`label-${tabs[index].key}`}
                aria-hidden
                accessible={false}
                accessibilityElementsHidden
                importantForAccessibility="no-hide-descendants"
                pointerEvents="none"
                style={[
                  a.absolute,
                  a.align_center,
                  a.justify_center,
                  {
                    zIndex: 3,
                    left: layout.x,
                    top: layout.y,
                    width: layout.width,
                    height: layout.height,
                  },
                ]}>
                <PillLabel
                  label={tabs[index].label}
                  index={index}
                  lastIndex={tabOffsets.length - 1}
                  dragProgress={dragProgress}
                  activeColor={activeLabelColor}
                  inactiveColor={t.atoms.text_contrast_high.color}
                />
              </View>
            ))}
          </View>
        </DraggableScrollView>
      </BlockDrawerGesture>

      {IS_WEB && canScrollLeft && (
        <View
          style={[
            a.absolute,
            a.left_0,
            {top: pillTop},
            {paddingLeft: gutterWidth},
            a.pr_md,
            a.z_10,
          ]}>
          <Button
            label={l`Scroll left`}
            onPress={scrollLeft}
            onPressIn={() => startContinuousScroll('left')}
            onPressOut={stopContinuousScroll}
            color="secondary"
            size="small"
            style={[
              a.border,
              t.atoms.border_contrast_low,
              t.atoms.bg,
              a.rounded_full,
              a.curve_continuous,
              {height: pillHeight, width: pillHeight},
            ]}>
            <ButtonIcon icon={ArrowLeft} />
          </Button>
        </View>
      )}

      {IS_WEB && canScrollRight && (
        <View
          style={[
            a.absolute,
            a.right_0,
            {top: pillTop},
            {paddingRight: gutterWidth},
            a.pl_md,
            a.z_10,
          ]}>
          <Button
            label={l`Scroll right`}
            onPress={scrollRight}
            onPressIn={() => startContinuousScroll('right')}
            onPressOut={stopContinuousScroll}
            color="secondary"
            size="small"
            style={[
              a.border,
              t.atoms.border_contrast_low,
              t.atoms.bg,
              a.rounded_full,
              a.curve_continuous,
              {height: pillHeight, width: pillHeight},
            ]}>
            <ButtonIcon icon={ArrowRight} />
          </Button>
        </View>
      )}
    </View>
  )
}

function TabPill({
  elementRef,
  tab,
  active,
  index,
  onLayout,
  onSelectTab,
  getAccessibilityHint,
}: {
  elementRef: React.Ref<React.ComponentRef<typeof View>>
  tab: TabPillItem
  active: boolean
  index: number
  onLayout: () => void
  onSelectTab: (index: number) => void
  getAccessibilityHint?: (tab: TabPillItem) => string
}) {
  const {t: l} = useLingui()

  return (
    <View ref={elementRef} onLayout={onLayout}>
      <Pressable
        accessibilityLabel={
          active ? l`${tab.label} tab, selected` : l`Select ${tab.label} tab`
        }
        accessibilityHint={
          getAccessibilityHint?.(tab) ?? l`Shows ${tab.label} notifications`
        }
        accessibilityRole="tab"
        accessibilityState={{selected: active}}
        onPress={() => onSelectTab(index)}
        style={[a.rounded_full, a.curve_continuous]}>
        <View
          style={[
            a.rounded_full,
            a.curve_continuous,
            a.px_lg,
            // 6px gives the designed 32px pill height
            {paddingVertical: 6},
            a.bg_transparent,
            {borderWidth: 1, borderColor: 'transparent'},
          ]}>
          <Text accessible={false} style={[a.font_medium, {opacity: 0}]}>
            {tab.label}
          </Text>
        </View>
      </Pressable>
    </View>
  )
}

type PillLayout = {
  x: number
  y: number
  width: number
  height: number
}

/** Hidden (`display: none`) or detached nodes measure as all zeros. */
function hasSize(rect: {width: number; height: number}) {
  return rect.width > 0 && rect.height > 0
}

function areLayoutsEqual(a: PillLayout[], b: PillLayout[]) {
  return (
    a.length === b.length &&
    a.every(
      (layout, index) =>
        layout.x === b[index].x &&
        layout.y === b[index].y &&
        layout.width === b[index].width &&
        layout.height === b[index].height,
    )
  )
}

/**
 * A CSS mask that fades the pills out under the scroll buttons on web, over
 * `width` from each side that can scroll. A mask rather than a background
 * over them, so it works over the header's see-through gradient.
 */
function getEdgeMask({
  left,
  right,
  width,
}: {
  left: boolean
  right: boolean
  width: number
}) {
  if (!left && !right) return undefined
  const solid = width * 0.7
  const start = left
    ? `transparent 0px, transparent ${solid}px, black ${width}px`
    : 'black 0px'
  const end = right
    ? `black calc(100% - ${width}px), transparent calc(100% - ${solid}px), transparent 100%`
    : 'black 100%'
  return `linear-gradient(to right, ${start}, ${end})`
}

/**
 * A tab's label, drawn over the pills. Its colour follows the sliding
 * indicator rather than the selected tab, so a label reads on whatever is
 * under it while the indicator moves, e.g. mid-swipe, or as it passes the tabs
 * in between after a tap.
 */
function PillLabel({
  label,
  index,
  lastIndex,
  dragProgress,
  activeColor,
  inactiveColor,
}: {
  label: string
  index: number
  /**
   * The indicator stops at the last tab, so the colour does too.
   */
  lastIndex: number
  dragProgress: SharedValue<number>
  activeColor: string
  inactiveColor: string
}) {
  const {fonts, flags} = useAlf()
  /*
   * Reanimated can't animate our `Text`, so this is React Native's text,
   * animated, with the styles `Text` would give the label. Unselectable `Text`
   * renders React Native's text too, so the label measures the same as the
   * hidden one that sizes its pill.
   */
  const textStyle = normalizeTextStyles(
    [a.text_sm, a.leading_snug, a.font_medium],
    {
      fontScale: fonts.scaleMultiplier,
      fontFamily: fonts.family,
      flags,
    },
  )
  const colorStyle = useAnimatedStyle(() => {
    const progress = Math.min(Math.max(dragProgress.get(), 0), lastIndex)
    return {
      /*
       * Fully active with the indicator on this tab, fading to inactive as the
       * indicator moves a whole tab away. Halfway there, the label sits about
       * half on the pill and half off it, so that colour has to read on both.
       * A plain RGB blend keeps it midway in lightness, where the default
       * gamma-corrected one leans towards the lighter end. Labels the
       * indicator isn't moving between stay put, so only two update at once.
       */
      color: interpolateColor(
        Math.abs(progress - index),
        [0, 1],
        [activeColor, inactiveColor],
        'RGB',
        {gamma: 1},
      ),
    }
  })

  return <Animated.Text style={[textStyle, colorStyle]}>{label}</Animated.Text>
}

function PillIndicator({
  layouts,
  dragProgress,
  backgroundColor,
  borderColor,
}: {
  layouts: PillLayout[]
  dragProgress: SharedValue<number>
  backgroundColor: string
  borderColor: string
}) {
  const height = layouts[0].height
  const radius = height / 2
  /**
   * Base width of the middle piece, which is stretched with scaleX. Layout
   * snaps it to whole physical pixels, so at fractional densities (e.g. 2.625x
   * on Android) a tiny base is resized by a large fraction, and once scaled
   * the middle falls short of the right cap or overshoots it. Scaling down from
   * the widest middle keeps that error under a pixel.
   */
  const middleWidth = Math.max(
    1,
    ...layouts.map(layout => layout.width - height),
  )
  /**
   * Snapped to whole pixels up front, so that translating the right cap by
   * `width - capWidth` lines its edge up exactly with the pill's.
   */
  const capWidth = PixelRatio.roundToNearestPixel(radius + 1)
  const inputRange = layouts.map((_, index) => index)
  const xOutputRange = layouts.map(layout => layout.x)
  const widthOutputRange = layouts.map(layout => layout.width)

  const geometry = useDerivedValue(() => {
    const progress = dragProgress.get()
    return {
      x: interpolate(progress, inputRange, xOutputRange, 'clamp'),
      width: interpolate(progress, inputRange, widthOutputRange, 'clamp'),
    }
  })

  const containerStyle = useAnimatedStyle(() => ({
    transform: [{translateX: geometry.get().x}],
  }))
  const middleStyle = useAnimatedStyle(() => ({
    transform: [
      {scaleX: Math.max(0.01, geometry.get().width - height) / middleWidth},
    ],
  }))
  const rightCapStyle = useAnimatedStyle(() => ({
    transform: [{translateX: geometry.get().width - capWidth}],
  }))

  return (
    <Animated.View
      accessible={false}
      pointerEvents="none"
      style={[
        a.absolute,
        a.curve_continuous,
        {
          zIndex: 1,
          top: layouts[0].y,
          left: 0,
          width: capWidth,
          height,
        },
        containerStyle,
      ]}>
      <View
        style={[
          a.curve_continuous,
          a.absolute,
          a.top_0,
          a.left_0,
          {
            width: capWidth,
            height,
            backgroundColor,
            borderColor,
            borderTopWidth: 1,
            borderBottomWidth: 1,
            borderLeftWidth: 1,
            borderTopLeftRadius: radius,
            borderBottomLeftRadius: radius,
          },
        ]}
      />
      <Animated.View
        style={[
          a.curve_continuous,
          a.absolute,
          a.top_0,
          {
            left: radius,
            width: middleWidth,
            height,
            transformOrigin: 'left center',
            backgroundColor,
            borderColor,
            borderTopWidth: 1,
            borderBottomWidth: 1,
          },
          middleStyle,
        ]}
      />
      <Animated.View
        style={[
          a.curve_continuous,
          a.absolute,
          a.top_0,
          a.left_0,
          {
            width: capWidth,
            height,
            backgroundColor,
            borderColor,
            borderTopWidth: 1,
            borderRightWidth: 1,
            borderBottomWidth: 1,
            borderTopRightRadius: radius,
            borderBottomRightRadius: radius,
          },
          rightCapStyle,
        ]}
      />
    </Animated.View>
  )
}
